import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';

import Modal from '../../containers/modal.jsx';
import {EXAMPLES, EXAMPLE_LEVELS} from '../../lib/examples/example-definitions';
import {getExamplePreviewXml} from '../../lib/examples/example-compiler';

import ExamplesPreviewWorkspace from './examples-preview-workspace.jsx';
import styles from './examples-library.css';

const ExamplesLibrary = ({
    isRtl,
    locale,
    mediaPath,
    onAddExample,
    onRequestClose,
    visible,
    vm
}) => {
    const [selectedLevel, setSelectedLevel] = useState(EXAMPLE_LEVELS[0].id);
    const [selectedExampleId, setSelectedExampleId] = useState(EXAMPLES[0].id);
    const [addingExampleId, setAddingExampleId] = useState(null);

    const levelExamples = useMemo(
        () => EXAMPLES.filter(example => example.level === selectedLevel),
        [selectedLevel]
    );

    useEffect(() => {
        if (!levelExamples.find(example => example.id === selectedExampleId) && levelExamples[0]) {
            setSelectedExampleId(levelExamples[0].id);
        }
    }, [levelExamples, selectedExampleId]);

    const selectedExample = useMemo(
        () => levelExamples.find(example => example.id === selectedExampleId) || levelExamples[0],
        [levelExamples, selectedExampleId]
    );

    const previewXml = useMemo(
        () => (selectedExample ? getExamplePreviewXml(selectedExample) : ''),
        [selectedExample]
    );

    if (!visible || !selectedExample) return null;

    const handlePreview = example => {
        setSelectedExampleId(example.id);
    };

    const handleAdd = async example => {
        setAddingExampleId(example.id);
        try {
            await onAddExample(example);
            onRequestClose();
        } finally {
            setAddingExampleId(null);
        }
    };

    return (
        <Modal
            fullScreen
            contentLabel="Libreria de ejemplos"
            id="examplesLibrary"
            onRequestClose={onRequestClose}
        >
            <div
                className={styles.body}
                dir={isRtl ? 'rtl' : 'ltr'}
            >
                <aside className={styles.catalogPanel}>
                    <div className={styles.catalogHeader}>
                        <h2 className={styles.catalogTitle}>Libreria de ejemplos</h2>
                        <p className={styles.catalogCopy}>
                            Treinta proyectos cortos, practicos y vistosos para Minecraft en Scratch.
                        </p>
                        <div className={styles.levelTabs}>
                            {EXAMPLE_LEVELS.map(level => {
                                const levelCount = EXAMPLES.filter(example => example.level === level.id).length;
                                const isActive = level.id === selectedLevel;
                                return (
                                    <button
                                        key={level.id}
                                        className={`${styles.levelTab} ${isActive ? styles.levelTabActive : ''}`}
                                        type="button"
                                        onClick={() => setSelectedLevel(level.id)}
                                    >
                                        {`${level.label} (${levelCount})`}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.examplesList}>
                        {levelExamples.map((example, index) => {
                            const isActive = selectedExample.id === example.id;
                            const isAdding = addingExampleId === example.id;
                            return (
                                <article
                                    key={example.id}
                                    className={`${styles.exampleCard} ${isActive ? styles.exampleCardActive : ''}`}
                                >
                                    <div className={styles.exampleCardHead}>
                                        <span className={styles.exampleNumber}>{index + 1}</span>
                                        <div>
                                            <h3 className={styles.exampleTitle}>{example.title}</h3>
                                            <p className={styles.exampleDescription}>{example.description}</p>
                                        </div>
                                    </div>
                                    <div className={styles.exampleActions}>
                                        <button
                                            className={`${styles.cardButton} ${styles.previewButton}`}
                                            type="button"
                                            onClick={() => handlePreview(example)}
                                        >
                                            Vista previa
                                        </button>
                                        <button
                                            className={`${styles.cardButton} ${styles.addButton}`}
                                            disabled={Boolean(addingExampleId)}
                                            type="button"
                                            onClick={() => handleAdd(example)}
                                        >
                                            {isAdding ? 'Anadiendo...' : 'Anadir'}
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </aside>

                <section className={styles.previewPanel}>
                    <div className={styles.previewShell}>
                        <div className={styles.previewTop}>
                            <div>
                                <p className={styles.previewKicker}>
                                    {EXAMPLE_LEVELS.find(level => level.id === selectedExample.level).label}
                                </p>
                                <h2 className={styles.previewTitle}>{selectedExample.title}</h2>
                                <p className={styles.previewDescription}>{selectedExample.description}</p>
                                <p className={styles.previewTip}>
                                    Anadir inserta los bloques en el sprite actual. Si estas en la escena, usa el primer sprite disponible.
                                </p>
                            </div>
                            <button
                                className={styles.previewAddButton}
                                disabled={Boolean(addingExampleId)}
                                type="button"
                                onClick={() => handleAdd(selectedExample)}
                            >
                                {addingExampleId === selectedExample.id ? 'Anadiendo...' : 'Anadir a Scratch'}
                            </button>
                        </div>

                        <div className={styles.workspaceCard}>
                            <div className={styles.workspaceHeader}>
                                <p className={styles.workspaceTitle}>Vista previa de bloques</p>
                                <span className={styles.workspaceBadge}>{selectedExample.id}</span>
                            </div>
                            {previewXml ? (
                                <ExamplesPreviewWorkspace
                                    isRtl={isRtl}
                                    locale={locale}
                                    mediaPath={mediaPath}
                                    vm={vm}
                                    xml={previewXml}
                                />
                            ) : (
                                <div className={styles.emptyState}>No hay bloques para mostrar.</div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </Modal>
    );
};

ExamplesLibrary.propTypes = {
    isRtl: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    mediaPath: PropTypes.string.isRequired,
    onAddExample: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    visible: PropTypes.bool,
    vm: PropTypes.shape({
        runtime: PropTypes.object
    }).isRequired
};

export default ExamplesLibrary;
